// src/api/commerceIndicators.js
//
// Cliente HTTP de los indicadores del portal de COMERCIO
// (microservice-reports → /reports/commerce-indicators/*).
//
// Contrato relevante para el front:
//  - El comercio se deriva del JWT: NO se envía companyId ni timezone.
//  - El backend devuelve las horas locales YA RESUELTAS ("HH:mm" y minuto del
//    día). El front NO debe reconvertir nada a otra zona horaria.
//  - Errores esperables: 400 (rango > 92 días, startDate > endDate, rango
//    entero anterior a reliableFrom) y 403 (el usuario no es de comercio). En
//    esos casos el backend manda { message } y ese texto es el que hay que
//    mostrarle al usuario: explica QUÉ pasó con su rango.
//
// Estilo de retorno igual que src/api/inform.js: en éxito devuelve el payload;
// en error devuelve { error: { message, status } } y nunca propaga la
// excepción, para que la vista decida cómo degradar.

import Axios from "../config/axios-config";

const BASE_PATH = "reports/commerce-indicators";

// Timeout explícito: la política del proyecto es no depender del default de la
// librería en ninguna llamada de red. Estas dos agregaciones pueden tardar en
// rangos largos (hasta 92 días), de ahí el margen sobre el default de 60 s.
const REQUEST_TIMEOUT_MS = 90000;

/**
 * Normaliza cualquier fallo de axios a { error: { message, status } }.
 * Prioriza el mensaje del backend porque es el que explica el problema real
 * (rango demasiado largo, datos no confiables antes de X, sin permisos).
 *
 * @param {unknown} err        error capturado
 * @param {string}  fallbackMsg mensaje a usar si el backend no mandó ninguno
 * @returns {{ error: { message: string, status: number|null } }}
 */
const toErrorResult = (err, fallbackMsg) => {
  const data = err?.response?.data;
  const message =
    (typeof data?.message === "string" && data.message) ||
    (Array.isArray(data?.message) && data.message.join(" ")) ||
    (typeof data?.error === "string" && data.error) ||
    err?.message ||
    fallbackMsg;

  return {
    error: {
      message: message || fallbackMsg,
      status: err?.response?.status ?? null,
    },
  };
};

/**
 * Agregados del período: horas de retiro y entrega, entregas fuera de corte,
 * lead time, fallidas, on-time, corte por operador y tendencia semanal.
 *
 * @param {{ startDate: string, endDate: string }} params fechas "YYYY-MM-DD"
 * @returns {Promise<object|{error:{message:string,status:number|null}}>}
 */
export const getCommerceIndicatorsSummary = async ({ startDate, endDate }) => {
  try {
    const response = await Axios.get(`${BASE_PATH}/summary`, {
      params: { startDate, endDate },
      timeout: REQUEST_TIMEOUT_MS,
    });
    return response.data;
  } catch (err) {
    console.error("Error al obtener el resumen de indicadores:", err);
    return toErrorResult(err, "No se pudo obtener el resumen de indicadores.");
  }
};

/**
 * Nube de puntos entrega-por-entrega: un bucket por (día, minuto del día,
 * tipo de evento, operador) con su conteo.
 *
 * @param {{ startDate: string, endDate: string }} params fechas "YYYY-MM-DD"
 * @returns {Promise<object|{error:{message:string,status:number|null}}>}
 */
export const getCommerceIndicatorsScatter = async ({ startDate, endDate }) => {
  try {
    const response = await Axios.get(`${BASE_PATH}/scatter`, {
      params: { startDate, endDate },
      timeout: REQUEST_TIMEOUT_MS,
    });
    return response.data;
  } catch (err) {
    console.error("Error al obtener la dispersión de indicadores:", err);
    return toErrorResult(err, "No se pudo obtener el gráfico de indicadores.");
  }
};
